import { useState, useEffect } from 'react';
import Navbar from '../home/components/Navbar';
import Footer from '../home/components/Footer';
import HeroSection from './components/HeroSection';
import FamilyHolidayIntro from './components/FamilyHolidayIntro';
import CountryTabs from './components/CountryTabs';
import AccommodationGrid from './components/AccommodationGrid';
import DestinationHighlights from './components/DestinationHighlights';
import WhyMuslimFriendly from './components/WhyMuslimFriendly';
import TravelTips from './components/TravelTips';
import CTASection from './components/CTASection';
import PhotoGallery from './components/PhotoGallery';

export default function DestinationPage() {
  const [selectedCountry, setSelectedCountry] = useState('saudi-arabia');

  useEffect(() => {
    window.scrollTo(0, 0);
    const siteUrl = import.meta.env.VITE_SITE_URL || 'https://www.alwaz.travel';
    const scripts: HTMLScriptElement[] = [];

    const schemas = [
      {
        '@context': 'https://schema.org',
        '@type': 'WebPage',
        name: 'Muslim-Friendly Family Holidays | Top 10 Destinations | Alwaz Travel',
        description: 'Discover the top 10 Muslim-friendly family holiday destinations from the UK. Halal food, prayer facilities, family-safe attractions, and cultural comfort in Saudi Arabia, Turkey, UAE, Morocco, Egypt, Jordan, Malaysia, Indonesia, Maldives, and Bosnia.',
        url: `${siteUrl}/family-holidays-destinations`,
        isPartOf: { '@type': 'WebSite', name: 'Alwaz Travels', url: siteUrl },
        breadcrumb: {
          '@type': 'BreadcrumbList',
          itemListElement: [
            { '@type': 'ListItem', position: 1, name: 'Home', item: siteUrl },
            { '@type': 'ListItem', position: 2, name: 'Family Holidays', item: `${siteUrl}/family-holidays-destinations` },
          ],
        },
      },
      {
        '@context': 'https://schema.org',
        '@type': 'ItemList',
        name: 'Top 10 Muslim-Friendly Family Holiday Destinations from UK',
        description: 'Handpicked Muslim-friendly family holiday destinations offering halal dining, prayer facilities, family-safe attractions, and cultural comfort for UK Muslim families.',
        url: `${siteUrl}/family-holidays-destinations`,
        numberOfItems: 10,
        itemListElement: [
          { '@type': 'ListItem', position: 1, item: { '@type': 'TouristDestination', name: 'Saudi Arabia', description: 'Spiritual family travel to Makkah, Madinah, Riyadh, and AlUla with halal food and prayer facilities', url: `${siteUrl}/family-holidays-destinations` } },
          { '@type': 'ListItem', position: 2, item: { '@type': 'TouristDestination', name: 'Turkey', description: 'Muslim-friendly family holidays in Istanbul, Antalya, and Cappadocia with halal dining and cultural attractions', url: `${siteUrl}/family-holidays-destinations` } },
          { '@type': 'ListItem', position: 3, item: { '@type': 'TouristDestination', name: 'Dubai, UAE', description: 'Luxury Muslim-friendly family holidays in Dubai and Abu Dhabi with halal food and family attractions', url: `${siteUrl}/family-holidays-destinations` } },
          { '@type': 'ListItem', position: 4, item: { '@type': 'TouristDestination', name: 'Morocco', description: 'Family-friendly Muslim travel to Marrakesh, Fes, and the Sahara with authentic halal cuisine', url: `${siteUrl}/family-holidays-destinations` } },
          { '@type': 'ListItem', position: 5, item: { '@type': 'TouristDestination', name: 'Egypt', description: 'Muslim family holidays exploring ancient history and Red Sea resorts with halal dining', url: `${siteUrl}/family-holidays-destinations` } },
          { '@type': 'ListItem', position: 6, item: { '@type': 'TouristDestination', name: 'Jordan', description: 'Family travel to Petra, Wadi Rum, and the Dead Sea in a Muslim-friendly environment', url: `${siteUrl}/family-holidays-destinations` } },
          { '@type': 'ListItem', position: 7, item: { '@type': 'TouristDestination', name: 'Malaysia', description: 'Halal food capital with family beaches, theme parks, and Muslim-majority culture in Kuala Lumpur and Langkawi', url: `${siteUrl}/family-holidays-destinations` } },
          { '@type': 'ListItem', position: 8, item: { '@type': 'TouristDestination', name: 'Indonesia', description: 'Muslim-friendly family resorts in Lombok and Bali with halal dining and stunning nature', url: `${siteUrl}/family-holidays-destinations` } },
          { '@type': 'ListItem', position: 9, item: { '@type': 'TouristDestination', name: 'Maldives', description: 'Luxury Muslim-friendly overwater villas with halal food resorts and pristine family beaches', url: `${siteUrl}/family-holidays-destinations` } },
          { '@type': 'ListItem', position: 10, item: { '@type': 'TouristDestination', name: 'Bosnia &amp; Herzegovina', description: 'European Muslim heritage in Sarajevo and Mostar with halal food and family-friendly culture', url: `${siteUrl}/family-holidays-destinations` } },
        ],
      },
      {
        '@context': 'https://schema.org',
        '@type': 'FAQPage',
        mainEntity: [
          {
            '@type': 'Question',
            name: 'What makes these destinations Muslim-friendly for families?',
            acceptedAnswer: {
              '@type': 'Answer',
              text: 'All destinations offer guaranteed halal dining options, easy access to prayer facilities and mosques, family-safe environments, and cultures that respect Islamic values and modest dress. Many are Muslim-majority countries or have significant Muslim populations.',
            },
          },
          {
            '@type': 'Question',
            name: 'Are these destinations safe for children?',
            acceptedAnswer: {
              '@type': 'Answer',
              text: 'Yes, all 10 destinations have been carefully selected for their low crime rates, family-friendly public spaces, and reputations for welcoming tourists with children. We prioritize destinations known for safety and hospitality.',
            },
          },
          {
            '@type': 'Question',
            name: 'Can I find halal food easily in these destinations?',
            acceptedAnswer: {
              '@type': 'Answer',
              text: 'Absolutely. Each destination offers certified halal restaurants, hotels with halal breakfast options, and local Muslim-owned eateries. Countries like Malaysia and Indonesia are renowned as halal food capitals.',
            },
          },
        ],
      },
    ];

    schemas.forEach((schema) => {
      const script = document.createElement('script');
      script.type = 'application/ld+json';
      script.textContent = JSON.stringify(schema);
      document.head.appendChild(script);
      scripts.push(script);
    });

    document.title = 'Muslim-Friendly Family Holidays | Top 10 Destinations | Alwaz Travel';
    const metaDesc = document.querySelector('meta[name="description"]');
    if (metaDesc) metaDesc.setAttribute('content', 'Discover the top 10 Muslim-friendly family holiday destinations from the UK. Halal food guaranteed, prayer facilities, family-safe attractions, and cultural comfort in Saudi Arabia, Turkey, UAE, Morocco, Egypt, Jordan, Malaysia, Indonesia, Maldives, and Bosnia. Perfect for UK Muslim families traveling with children.');

    const metaKeywords = document.querySelector('meta[name="keywords"]');
    if (metaKeywords) {
      metaKeywords.setAttribute('content', 'Muslim family holidays, halal holidays UK, Muslim-friendly destinations, family holidays with halal food, Islamic family travel, prayer facilities holidays, Muslim family vacation, halal resorts, family holidays from UK, Muslim travel destinations');
    } else {
      const keywords = document.createElement('meta');
      keywords.name = 'keywords';
      keywords.content = 'Muslim family holidays, halal holidays UK, Muslim-friendly destinations, family holidays with halal food, Islamic family travel, prayer facilities holidays, Muslim family vacation, halal resorts, family holidays from UK, Muslim travel destinations';
      document.head.appendChild(keywords);
    }

    return () => {
      scripts.forEach((s) => s.parentNode?.removeChild(s));
    };
  }, []);

  return (
    <div className="min-h-screen bg-white">
      <Navbar />
      <HeroSection selectedCountry={selectedCountry} />
      <FamilyHolidayIntro />
      <CountryTabs selectedCountry={selectedCountry} onSelectCountry={setSelectedCountry} />
      <AccommodationGrid selectedCountry={selectedCountry} />
      <DestinationHighlights selectedCountry={selectedCountry} />
      <PhotoGallery selectedCountry={selectedCountry} />
      <WhyMuslimFriendly />
      <TravelTips selectedCountry={selectedCountry} />
      <CTASection />
      <Footer />
    </div>
  );
}